import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SECTOR_META } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { ProjetBadge } from "@/components/ProjetBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Loader2, ArrowRight, Home, Building2, Factory, Sprout, Flame } from "lucide-react";

const SECTOR_ICON = { BAR: Home, BAT: Building2, IND: Factory, AGRI: Sprout, RES: Flame };
const TABS = [
  { code: "BAR", label: "Résidentiel" }, { code: "BAT", label: "Tertiaire" }, { code: "IND", label: "Industrie" },
  { code: "AGRI", label: "Agricole" }, { code: "RES", label: "Réseau de chaleur" },
];

export default function Catalogue() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const allowed = user?.partner?.allowed_sectors;
  const [fiches, setFiches] = useState(null);
  const [sector, setSector] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/fiches").then((r) => setFiches(r.data));
  }, []);

  const filtered = useMemo(() => {
    if (!fiches) return [];
    return fiches.filter((f) => {
      const okSector = sector === "all" || f.sector === sector;
      const okQ =
        f.code.toLowerCase().includes(q.toLowerCase()) ||
        f.name.toLowerCase().includes(q.toLowerCase()) ||
        f.category.toLowerCase().includes(q.toLowerCase());
      return okSector && okQ;
    });
  }, [fiches, sector, q]);

  if (!fiches) {
    return (
      <div className="grid place-items-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-genis" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-genis">Référentiel</p>
        <h1 className="text-3xl font-extrabold font-heading tracking-tight text-genis-deep dark:text-white">Catalogue des fiches</h1>
        <p className="text-muted-foreground mt-1">Fiches d'opérations standardisées {TABS.filter((t) => !allowed || allowed.includes(t.code)).map((t) => t.label).join(" · ")} avec formules officielles.</p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <Tabs value={sector} onValueChange={setSector} className="w-full md:w-auto">
          <TabsList data-testid="catalogue-sector-tabs">
            <TabsTrigger value="all" data-testid="sector-tab-all">Tous</TabsTrigger>
            {TABS.filter((t) => !allowed || allowed.includes(t.code)).map((t) => (
              <TabsTrigger key={t.code} value={t.code} data-testid={`sector-tab-${t.code.toLowerCase()}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un code ou une opération…" className="pl-9" data-testid="catalogue-search-input" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((f) => {
          const meta = SECTOR_META[f.sector] || { color: "#33ADF5", badge: "bg-slate-100 text-slate-700 border-slate-200" };
          const Icon = SECTOR_ICON[f.sector] || Home;
          return (
            <Card key={f.code} className="p-5 flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-all border-border" data-testid={`fiche-card-${f.code}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="h-10 w-10 grid place-items-center rounded-lg shrink-0" style={{ background: `${meta.color}18`, color: meta.color }}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge className={`${meta.badge} border`}>{f.code}</Badge>
                  <ProjetBadge show={f.projet} code={f.code} />
                  {f.version && (
                    <span className="text-[10px] font-semibold text-muted-foreground" data-testid={`fiche-version-${f.code}`} title="Version d'arrêté (partie A) — applicable selon la date d'engagement">
                      version {f.version}
                    </span>
                  )}
                </div>
              </div>
              <h3 className="mt-3 font-heading font-bold text-genis-deep dark:text-white leading-snug">{f.name}</h3>
              <div className="mt-1 text-xs font-medium" style={{ color: meta.color }}>{f.category} · {f.sector_label}</div>
              <p className="mt-2 text-sm text-muted-foreground flex-1">{f.description}</p>
              <div className="mt-3 rounded-lg bg-genis/8 px-3 py-2 text-xs font-semibold text-genis-dark dark:text-genis" data-testid={`prime-hint-${f.code}`}>
                Prime indicative : {f.prime_hint || f.unit_hint}
              </div>
              <Button variant="outline" className="mt-4 w-full hover:border-genis hover:text-genis" onClick={() => navigate(`/simulation/new?fiche=${f.code}`)} data-testid={`fiche-use-${f.code}`}>
                Simuler cette opération <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
